// "Publish to reports" (2026-11-01, GPS feed Stage 2; admins only, checked on the server): preview what this StatSports
// session writes into the GPS & Load report session, confirm, see the status ("changed since publish" after an edit:
// republish explicitly), unpublish. The choices the preview asks for: who a recording belongs to (a swapped vest:
// the CSV's player by default), whether a CSV-only player keeps his rows (yes by default), goalkeeper sessions (left out
// by default). Kept CSV rows (above Sonra, hand additions, corrected max speed, invalid windows) are listed, never refused.
// Feed Stage 3a (2026-11-04, design §20.2–20.3): the "Data quality" block (partial, missing data, poor position quality,
// no raw data: markers only, the values stay in every total) and "Compare with CSV" (published or preview values next
// to the CSV rows, each difference with its cause; while published the CSV rows come from the publication's snapshot).
import React, { useCallback, useEffect, useState } from 'react';
import { previewFeed, getFeedStatus, publishFeed, unpublishFeed, compareFeedWithCsv } from '../../../api';
import { fmtNum } from './seShared';

const Err = ({ msg }) => (msg ? <div className="se-error" style={{ marginTop: 6 }}>{msg}</div> : null);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const STATE_PILL = { 'not published': '', published: 'se-pill--est', 'changed since publish': 'se-pill--warn' };

// the options a preview/publish sends, from the stored ones (status) or the defaults
export const startOptions = stored => ({
  goalkeepers: !!(stored && stored.goalkeepers), blocks: !!(stored && stored.blocks), gpsSessionId: stored && stored.gpsSessionId != null ? stored.gpsSessionId : null,
  owners: {}, dropCsvPlayers: ((stored && stored.dropCsvPlayers) || []).map(Number),
});

export function rowsLine(plan) {
  if (!plan) return '';
  return `writes ${plural(plan.write.sessionRows, 'session row')} and ${plural(plan.write.drillRows, 'drill row')}; replaces ${plan.replace.sessionRows} + ${plan.replace.drillRows} CSV rows `
    + `(kept for unpublish); keeps ${plan.keep.sessionRows} + ${plan.keep.drillRows} CSV rows as they are`
    + `${plan.superseded && plan.superseded.sessionRows + plan.superseded.drillRows ? `; ${plan.superseded.sessionRows + plan.superseded.drillRows} of the replaced rows are KNVB estimates superseded by measured halves` : ''}`;
}

// ── Data quality (Stage 3a) ─────────────────────────────────────────────────────────────────────────────────────
const DQ = [['partial', 'partial', 'recording covers less than 98 % of the window'], ['missingData', 'missing data', '5 % or more of the samples without position'],
  ['poorPosition', 'poor position quality', 'positions jumping, spread or off the pitch'], ['noRawData', 'no raw GPS', 'Sonra\'s values only']];
const pct = v => (v == null ? '' : ` ${Math.round(v * 100)} %`);
export function DataQuality({ dq, labelOfPlayer }) {
  if (!dq) return null;
  const any = DQ.some(([k]) => dq[k] && dq[k].length);
  return (
    <div style={{ marginTop: 6 }} data-testid="data-quality">
      <strong>Data quality</strong> <span className="se-muted">· markers on the published rows; the values stay in every total</span>
      {!any && <div className="se-muted">no partial recordings, no missing data, no poor position quality</div>}
      <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
        {DQ.filter(([k]) => dq[k] && dq[k].length).map(([k, label, hint]) => (
          <li key={k}><span className="se-pill se-pill--warn" title={hint}>{label}</span> {dq.counts[k].players} player{dq.counts[k].players === 1 ? '' : 's'}, {dq.counts[k].rows} row{dq.counts[k].rows === 1 ? '' : 's'}:{' '}
            <span className="se-muted">{dq[k].map(x => `${labelOfPlayer(x.playerId)} ${x.title}${k === 'partial' ? pct(x.coverage) : k === 'missingData' ? pct(x.zeroPosShare) : ''}`).join(' · ')}</span></li>
        ))}
      </ul>
    </div>
  );
}

// ── Compare with CSV (Stage 3a) ─────────────────────────────────────────────────────────────────────────────────
const CMP_COLS = [['time', m => (m === 'total_time' || m === 'minutes_played')], ['TD', 'td'], ['RD', 'rd'], ['HID', 'hid'], ['SD', 'sd'], ['SA', 'sa'], ['Expl', 'expl'],
  ['HMLD', 'hmld'], ['Max', 'max_speed'], ['avg HR', 'avg_hr'], ['max HR', 'max_hr']];
const cellOf = (row, col) => row.cells.find(c => (typeof col === 'function' ? col(c.metric) : c.metric === col));
const show = (m, v) => (v == null ? '–' : m === 'total_time' ? String(v).slice(0, 8) : m === 'max_speed' ? fmtNum(Number(v), 1) : fmtNum(Number(v), 0));
const STATUS = { identical: 'identical', tolerance: 'within tolerance', different: 'different', kept: 'kept CSV row', not_in_csv: 'not in the CSV', csv_only: 'CSV row not published' };
export function CompareWithCsv({ result, labelOfPlayer }) {
  const [onlyDiff, setOnlyDiff] = useState(true);
  const rows = result.rows.filter(r => !onlyDiff || r.status !== 'identical');
  const sum = k => `${result.summary[k].rows} · identical ${result.summary[k].identical} · within tolerance ${result.summary[k].tolerance} · different ${result.summary[k].different} · kept ${result.summary[k].kept}`
    + ` · not in the CSV ${result.summary[k].not_in_csv} · not published ${result.summary[k].csv_only}`;
  return (
    <div style={{ marginTop: 6 }} data-testid="compare-csv">
      <div className="se-bar"><strong>Compare with CSV</strong>
        <span className="se-muted">{result.state === 'published' ? 'published values' : result.state === 'changed since publish' ? 'preview values (changed since publish)' : 'preview values'} against the CSV rows ({result.csvSource === 'snapshot' ? 'from the publication\'s snapshot' : 'live'})</span>
        <label className="se-muted"><input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} /> only differences</label></div>
      <div className="se-muted">session rows {sum('session')}</div>
      <div className="se-muted">drill rows {sum('drill')}</div>
      {Object.keys(result.causes).length > 0 && <div className="se-muted">causes: {Object.entries(result.causes).map(([c, n]) => `${c} ${n}`).join(' · ')}</div>}
      <div style={{ overflowX: 'auto' }}>
        <table className="se-table" style={{ marginTop: 4 }}>
          <thead><tr><th className="l">Player</th><th className="l">Row</th>{CMP_COLS.map(([h]) => <th key={h}>{h}</th>)}<th className="l">Cause</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="l">{labelOfPlayer(r.playerId)}</td><td className="l">{r.title}{r.startTime ? ` ${r.startTime.slice(0, 5)}` : ''}</td>
                {r.cells.length ? CMP_COLS.map(([h, col]) => {
                  const c = cellOf(r, col);
                  if (!c) return <td key={h} />;
                  const bg = c.verdict === 'same' ? 'rgba(34,197,94,0.12)' : c.verdict === 'tolerance' ? 'rgba(34,197,94,0.05)' : 'rgba(245,158,11,0.18)';
                  return <td key={h} style={{ background: bg }} title={c.cause || ''}>{c.verdict === 'same' ? '✓' : `${show(c.metric, c.published)} | ${show(c.metric, c.csv)}`}</td>;
                }) : <td colSpan={CMP_COLS.length} className="l se-muted">{STATUS[r.status]}{r.note ? `: ${r.note}` : ''}</td>}
                <td className="l se-muted">{r.cells.length ? [...new Set(r.cells.map(c => c.cause).filter(Boolean))].join(', ') || STATUS[r.status] : ''}{r.cells.length && r.note ? ` · ${r.note}` : ''}</td>
              </tr>
            ))}
            {!rows.length && <tr><td className="l se-muted" colSpan={CMP_COLS.length + 3}>no differences</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="se-muted">✓ identical within the tolerance (0.5 unit or 0.05 %, total time ±1.5 s); otherwise published | CSV. Hover a cell for its cause.</div>
    </div>
  );
}

export default function PublishPanel({ token, sessionId, labelOfPlayer, onChanged, onClose }) {
  const [status, setStatus] = useState(null);
  const [options, setOptions] = useState(null);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(null);   // null | 'loading' | the comparison

  const loadStatus = useCallback(() => getFeedStatus(token, sessionId).then(s => { setStatus(s); return s; }), [token, sessionId]);
  useEffect(() => {
    let live = true;
    setStatus(null); setPreview(null); setOptions(null); setErr(''); setMsg(''); setCompare(null);
    loadStatus().then(s => { if (live) setOptions(startOptions(s.options)); }).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [loadStatus]);
  useEffect(() => {
    if (!options) return undefined;
    let live = true;
    setPreview(null); setCompare(null);
    previewFeed(token, sessionId, options).then(p => live && setPreview(p)).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [token, sessionId, options]);

  const reload = () => { setOptions(o => ({ ...o })); return loadStatus(); };
  const act = (fn, done) => {
    setBusy(true); setErr(''); setMsg('');
    fn().then(r => { setMsg(done(r)); return reload(); }).then(() => onChanged && onChanged())
      .catch(e => { setErr(e.message); if (e.status === 409) reload().catch(() => {}); })
      .finally(() => setBusy(false));
  };
  const state = status ? status.state : null;
  const published = state && state !== 'not published';
  const publish = () => {
    const what = published ? 'Republish' : 'Publish';
    const ok = window.confirm(`${what} this session to the GPS & Load reports?\n\n${rowsLine(preview.plan)}.\n\n`
      + 'While it is published, CSV uploads, deletes and max-speed edits of the report session are refused; unpublish puts the CSV rows back exactly.');
    if (ok) act(() => publishFeed(token, sessionId, options, preview.inputHash), r => `${r.republished ? 'Republished' : 'Published'}${r.created ? ' (new report session)' : ''}: ${rowsLine(r.summary)}.`);
  };
  const unpublish = () => {
    const ok = window.confirm('Unpublish this session?\n\nThe published rows are removed and the CSV rows come back exactly as they were (a note written since stays).');
    if (ok) act(() => unpublishFeed(token, sessionId), r => `Unpublished: ${r.deleted.data + r.deleted.drills} rows removed, ${r.restored.data + r.restored.drills} CSV rows back`
      + `${r.sessionRemoved ? '; the session it created is removed' : r.sessionKeptBecause ? `; the session stays (${r.sessionKeptBecause})` : ''}.`
      + `${r.mss && r.mss.skipped.length ? ` MSS values not put back for ${r.mss.skipped.map(x => `${labelOfPlayer(x.playerId)} (${x.reason})`).join(', ')}.` : ''}`);
  };
  const setOwner = (sp, pid) => setOptions(o => ({ ...o, owners: { ...o.owners, [sp]: Number(pid) } }));
  const loadCompare = () => { setCompare('loading'); compareFeedWithCsv(token, sessionId, options).then(setCompare).catch(e => { setCompare(null); setErr(e.message); }); };
  const toggleDrop = pid => setOptions(o => ({ ...o, dropCsvPlayers: o.dropCsvPlayers.includes(pid) ? o.dropCsvPlayers.filter(x => x !== pid) : [...o.dropCsvPlayers, pid] }));

  const p = preview;
  const blocks = p ? p.warnings.filter(w => w.severity === 'block') : [];
  const upToDate = !!(p && p.published && p.published.upToDate);   // with the options as shown now (a changed choice → republish)
  return (
    <div className="se-sub" data-testid="publish-panel">
      <div className="se-bar" style={{ justifyContent: 'space-between' }}>
        <strong>Publish to reports</strong>
        {state && <span className={`se-pill ${STATE_PILL[state] || ''}`} data-testid="publish-state">{state}</span>}
      </div>
      {status && status.publication && (
        <div className="se-muted">published {String(status.publication.publishedAt).slice(0, 16).replace('T', ' ')} into report session #{status.publication.reportSessionId}
          {status.publication.createdSession ? ' (created by the publish)' : ''}{state === 'changed since publish' ? ': the periods or the data changed since; republish to update the reports' : ''}</div>
      )}
      <Err msg={err} />
      {msg && <div className="se-banner" style={{ marginTop: 6 }}>{msg}</div>}
      {!p ? (!err && <div className="se-muted">Preparing the preview…</div>) : (
        <>
          <div style={{ marginTop: 6 }}>
            Report session: {p.session.create ? <strong>a new {p.session.sessionType} session</strong> : <strong>#{p.session.id}</strong>} · {p.session.date} · {p.session.title}
            {p.link.overlap != null && <span className="se-muted"> · player overlap {Math.round(p.link.overlap * 100)} %</span>}
          </div>
          <div className="se-muted" data-testid="publish-rows">{rowsLine(p.plan)}</div>
          <div className="se-muted">{p.link.feeds.map(f => `${f.name} (${f.role}${f.included ? '' : ', left out'})`).join(' · ')}</div>
          <label className="se-muted"><input type="checkbox" checked={options.goalkeepers} onChange={e => setOptions(o => ({ ...o, goalkeepers: e.target.checked }))} /> include goalkeeper sessions</label>
          {/* timed blocks (2026-11-03): the 5-minute periods made with "Split into blocks"; left out unless ticked (Q11) */}
          <label className="se-muted" style={{ marginLeft: 12 }}><input type="checkbox" checked={options.blocks} onChange={e => setOptions(o => ({ ...o, blocks: e.target.checked }))} /> include timed blocks (5-minute periods)</label>

          {blocks.map(w => <div key={w.code} className="se-error" style={{ marginTop: 6 }}>{w.message}</div>)}
          <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
            {p.warnings.filter(w => w.severity !== 'block').map((w, i) => (
              <li key={`${w.code}-${i}`} className="se-muted">{w.message}{w.count != null ? ` (${w.count})` : ''}{w.players ? `: ${w.players.map(labelOfPlayer).join(', ')}` : ''}</li>
            ))}
          </ul>

          <DataQuality dq={p.dataQuality} labelOfPlayer={labelOfPlayer} />

          {p.owners && p.owners.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>Recording belongs to</strong>
              {p.owners.map(o => {
                const choices = [...new Set([o.playerId, o.statsportsPlayerId].filter(x => x != null))];
                return (
                  <div key={o.sessionPlayerId} className="se-bar" style={{ marginTop: 4 }}>
                    <span className="se-muted">the unit StatSports maps to {o.statsportsPlayerId != null ? labelOfPlayer(o.statsportsPlayerId) : 'nobody'}:</span>
                    <select className="se-input" aria-label="recording belongs to" value={o.playerId} disabled={busy} onChange={e => setOwner(o.sessionPlayerId, e.target.value)}>
                      {choices.map(pid => <option key={pid} value={pid}>{labelOfPlayer(pid)}{pid === o.statsportsPlayerId ? ' (StatSports mapping)' : ' (the CSV\'s player)'}</option>)}
                    </select>
                    <span className="se-muted">{o.how === 'default' ? 'default' : 'chosen'}</span>
                  </div>
                );
              })}
            </div>
          )}

          {p.csvOnlyPlayers.filter(c => c.sameRecordingAs == null).length > 0 && (
            <div style={{ marginTop: 6 }}>
              <strong>CSV rows without StatSports data</strong>
              {p.csvOnlyPlayers.filter(c => c.sameRecordingAs == null).map(c => (
                <div key={c.playerId}>
                  <label><input type="checkbox" checked={!options.dropCsvPlayers.includes(c.playerId)} disabled={busy} onChange={() => toggleDrop(c.playerId)} aria-label={`keep CSV rows of ${labelOfPlayer(c.playerId)}`} />
                    {' '}keep the CSV rows of {labelOfPlayer(c.playerId)}</label>
                  <span className="se-muted"> · {fmtNum(c.csvTd, 0)} m{c.imputed ? ` · estimated${c.method ? ` (${c.method})` : ''}` : ''}</span>
                </div>
              ))}
            </div>
          )}

          {(p.completionCandidates.length > 0 || p.maxSpeedEdits.length > 0 || p.invalidWindowRows.length > 0) && (
            <details style={{ marginTop: 6 }}>
              <summary>CSV rows kept or marked ({p.completionCandidates.length + p.maxSpeedEdits.length + p.invalidWindowRows.length})</summary>
              <table className="se-table"><tbody>
                {p.completionCandidates.map((c, i) => (
                  <tr key={`c${i}`}><td className="l">{labelOfPlayer(c.playerId)}</td><td className="l">{c.title}</td>
                    <td className="l">{c.row === 'extra' ? 'not in Sonra: kept' : `CSV ${fmtNum(c.csvTd, 0)} m > Sonra ${fmtNum(c.newTd, 0)} m: kept`}</td></tr>
                ))}
                {p.maxSpeedEdits.map((e, i) => (
                  <tr key={`m${i}`}><td className="l">{labelOfPlayer(e.playerId)}</td><td className="l">{e.title}{e.startTime ? ` ${e.startTime.slice(0, 5)}` : ''}</td>
                    <td className="l">max speed {fmtNum(e.csvKmh, 1)} km/h kept (Sonra {fmtNum(e.sonraKmh, 1)}), marked manual</td></tr>
                ))}
                {p.invalidWindowRows.map((r, i) => (
                  <tr key={`w${i}`}><td className="l">{labelOfPlayer(r.playerId)}</td><td className="l">{r.title}</td><td className="l">Sonra's values, marked "invalid window"</td></tr>
                ))}
              </tbody></table>
            </details>
          )}

          {/* match max speed (gaps build Stage C): his session row and Entire Session - Live row get the max of his own halves and runs */}
          {p.plan && p.plan.maxSpeedRule && p.plan.maxSpeedRule.applies && p.plan.maxSpeedRule.changes.length > 0 && (
            <details style={{ marginTop: 6 }} data-testid="max-speed-rule">
              <summary>Match max speed from his own halves and runs: {p.plan.maxSpeedRule.changes.length} change{p.plan.maxSpeedRule.changes.length === 1 ? '' : 's'}, {p.plan.maxSpeedRule.empty} empty</summary>
              <table className="se-table"><tbody>
                {p.plan.maxSpeedRule.changes.map(c => (
                  <tr key={c.playerId}><td className="l">{labelOfPlayer(c.playerId)}</td>
                    <td className="l">{c.before == null ? 'empty' : `${fmtNum(c.before, 1)} km/h`} → {c.after == null ? 'empty (no half, no run)' : `${fmtNum(c.after, 1)} km/h`}</td></tr>
                ))}
              </tbody></table>
            </details>
          )}

          <div className="se-bar" style={{ marginTop: 8 }}>
            <button className="se-btn se-btn--primary" disabled={busy || !p.canPublish || upToDate} onClick={publish}
              title={upToDate ? 'already published and up to date' : 'asks for confirmation first'}>{published ? 'Republish…' : 'Publish…'}</button>
            {published && <button className="se-btn" disabled={busy} onClick={unpublish}>Unpublish…</button>}
            {!p.session.create && <button className="se-btn" disabled={compare === 'loading'} onClick={() => (compare && compare !== 'loading' ? setCompare(null) : loadCompare())}
              title="the published (or preview) values next to the CSV rows of this report session">{compare && compare !== 'loading' ? 'Hide comparison' : compare === 'loading' ? 'Comparing…' : 'Compare with CSV'}</button>}
            <button className="se-btn" onClick={onClose}>Close</button>
          </div>
          {compare && compare !== 'loading' && <CompareWithCsv result={compare} labelOfPlayer={labelOfPlayer} />}
        </>
      )}
    </div>
  );
}
